<template>
	<div
		class="filter-list"
		:class="{
			'is-mobile': isMobileWebApp,
			'is-tagging-tool': isTaggingToolMode,
			[`-${modifier}`]: modifier,
		}"
	>
		<!-- header -->
		<div
			class="filter-list__header"
			:class="[
				`-pad-h-${headerPaddingHorizontal}`,
				`-pad-v-${headerPaddingVertical}`,
			]"
		>
			<div class="filter-list__header-left">
				<slot
					name="title"
					:title="title"
					:subtitle="subtitle"
				>
					<Title
						:title="title"
						:subtitle="subtitle"
						:style="{ visibility: hideTitleVisibility ? 'hidden' : 'visible' }"
					/>
				</slot>
				<slot name="over-title" />
			</div>
			<div
				v-if="$slots['filters-dropdown'] || $scopedSlots['filters-dropdown']"
				class="filter-list__header-right"
				data-qa="list-filter"
			>
				<slot
					name="filters-dropdown"
					:filters-module="filtersModule"
				/>
			</div>
		</div>
		<div
			v-if="!isTaggingToolMode"
			class="filter-list__divider"
		/>
		<slot name="filter-badges" />
		<div
			v-if="!isMobileWebApp && filterBadges.length > 0"
			class="filter-list__header-under"
			:class="[
				`-pad-h-${headerPaddingHorizontal}`,
			]"
		>
			<FilterBadges
				:badges="filterBadges"
				:clear-all-filters-label="labels.clearAllFilters"
				@remove-filter="handleRemoveFilter"
				@clear-filters="handleClearFilters"
			/>
		</div>
		<div
			v-if="!isTaggingToolMode && !isMobileWebApp && filterBadges.length > 0"
			class="filter-list__divider"
		/>
		<!-- list view -->
		<slot name="list-view" />
		<!-- footer -->
		<slot name="footer" />
	</div>
</template>

<script>
import FilterBadges from '../filter-badges';
import $I18n from '../../i18n';
import Title from '@videotools/js/components/Title/Title';
import { mapGetters } from 'vuex';

export default {
	name:       'FilterableList',
	components: {
		FilterBadges,
		Title,
	},
	props: {
		title: {
			type:    String,
			default: '',
		},
		subtitle: {
			type: String,
		},
		activeFilters: {
			type:    [Array, Object],
			default: null,
		},
		isFilter: {
			type:    Boolean,
			default: true,
		},
		labels: {
			type:    Object,
			default: () => ({
				cancelBtn:       $I18n.trans('commons.cancel'),
				clearAllFilters: $I18n.trans('one.clear_all_filters'),
			}),
		},
		dropdownDisabled: {
			type:    Boolean,
			default: false,
		},
		listContainerClass: {
			default: () => [],
		},
		listBodyClass: {
			default: () => [],
		},
		filterNoSelectionLabel: {
			type:    String,
			default: '',
		},
		filtersModule: {
			type:    String,
			default: null,
		},
		modifier: {
			type:      String,
			default:   'default',
			validator: (value) => {
				return ['default', 'highlight-tool', 'tag-list', 'clip-list', 'tag-template-list', 'videos'].includes(value);
			},
		},
		hideTitleVisibility: {
			type:    Boolean,
			default: false,
		},
		headerPaddingHorizontal: {
			type:      String,
			default:   'sm',
			validator: (value) => {
				return ['xs', 'sm', 'md', 'lg'].includes(value);
			},
		},
		headerPaddingVertical: {
			type:      String,
			default:   'sm',
			validator: (value) => {
				return ['xs', 'sm', 'md', 'lg'].includes(value);
			},
		},
	},
	computed: {
		...mapGetters({
			isMobileWebApp:    'app/isMobileWebApp',
			isTaggingToolMode: 'app/isTaggingToolMode',
		}),
		filterBadges() {
			if (!this.isFilter) {
				return [];
			}
			return this.activeFilters.map((f) => {
				return {
					id:    f.key,
					label: f.label,
				};
			});
		},
	},
	methods: {
		handleClearFilters() {
			this.handleActiveFiltersChanged([]);
		},
		handleRemoveFilter(id) {
			const activeFilters = this.activeFilters.filter((e) => e.key !== id);
			this.handleActiveFiltersChanged(activeFilters);
		},
		handleActiveFiltersChanged(filters) {
			const normalizedFilters = Array.isArray(filters) ? filters : [filters];
			this.$emit('filters-updated', normalizedFilters);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_layout';
@import '@videotools/sass/_fonts';

// .filter-list {}

.filter-list__header {
	display: flex;
	align-items: center;
	gap: $sp3;

	// &.-pad-h-sm {} // no padding horizontal for sm
	// &.-pad-v-sm {} // no padding vertical for sm

	&.-pad-h-sm {
		padding-left: 8px;
		padding-right: 8px;
	}

	&.-pad-h-md {
		padding-left: 16px;
		padding-right: 16px;
	}

	&.-pad-h-lg {
		padding-left: 24px;
		padding-right: 24px;
	}

	&.-pad-v-sm {
		padding-top: 8px;
		padding-bottom: 8px;
	}

	&.-pad-v-md {
		padding-top: 16px;
		padding-bottom: 16px;
	}

	&.-pad-v-lg {
		padding-top: 24px;
		padding-bottom: 24px;
	}
}

.filter-list__header-under {
	padding-top: $sp3;
	padding-bottom: $sp3;
	// &.-pad-h-sm {} // no padding horizontal for sm

	&.-pad-h-sm {
		padding-left: 8px;
		padding-right: 8px;
	}

	&.-pad-h-md {
		padding-left: 16px;
		padding-right: 16px;
	}

	&.-pad-h-lg {
		padding-left: 24px;
		padding-right: 24px;
	}
}

.filter-list__header-right {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	flex-shrink: 0;
}

.filter-list__header-left {
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	flex-grow: 1;
	overflow: hidden;
	min-height: 2.7em; // min height of the search input
}

.filter-list__divider {
	.filter-list:not(.is-tagging-tool) & {
		height: 1px;
		margin-left: 8px;
		margin-right: 8px;
		background: var(--color-grey-900);
	}
}
</style>
